import * as React from 'react'
import {
  AlertCircle as WarningCircleIcon,
  MapPin as MapPinAreaIcon,
  Package as PackageIcon,
  Workflow as FlowArrowIcon
} from 'lucide-react'
import { Link, useParams } from 'react-router-dom'
import { decodeRouteParam } from '@atbash-labs/plantview-links'
import {
  useGetPlantAreaDetailQuery,
  usePlantSessionPaint,
  collectAdditionalProperties,
  type PlantAreaSummary,
  type PlantEquipment,
  type PlantPartRef,
  type PlantProcess
} from '@axilon-apps/domains-ontology-ui-state'
import { Badge } from '../../components/ui/badge'
import { Empty } from '../../components/primitives/Empty'
import { plantAreasIndexHref } from '../../lib/plant-links'
import {
  areaKindIcon,
  areaKindLabel,
  temperatureClassIcon,
  temperatureClassLabel
} from './area-meta'
import { equipmentIconFor } from './equipment-icon'
import { EntityDetailsInspector } from './entity/EntityDetailsInspector'
import { FaultKnowledgeSection } from './equipment-relationships'
import { PlantStrayPill } from './entity/PlantStrayPill'
import { PlantHref } from './PlantHref'
import {
  SessionGhostTile,
  SessionMarkAffordances,
  SessionRollupPill,
  SessionTitleMark,
  countSessionMarks,
  sessionDetailHeaderClass,
  sessionMarkClass,
  useVisibleGhosts,
  type PlantSessionGhostView
} from './session-mark-ui'

const GhostEquipmentIcon = equipmentIconFor(undefined)

/**
 * `/plant/areas/:areaId` — single-area detail.
 *
 * Renders the area's identity (icon + name + kind + temperature-class
 * chips), KPI stats for the area's surroundings, sub-area drill-down
 * grid, and lists of every `Equipment`, `Process`, and `Part` placed
 * directly in this area via `LOCATED_IN`. Nested sub-area contents
 * are intentionally hidden here — they surface on each sub-area's
 * own page so the drill-down stays a tree instead of a flood.
 *
 * When a session is painting, the header / tiles carry marks, stats
 * gain +/~/− deltas, and deleted children render as ghost tiles in
 * each section.
 */
const AreaDetailPage = (): React.ReactElement => {
  const { areaId = '' } = useParams<{ areaId: string }>()
  const decodedAreaId = decodeRouteParam(areaId)

  const { data, isLoading, isError } = useGetPlantAreaDetailQuery(decodedAreaId, {
    skip: !decodedAreaId
  })
  const { active, markFor } = usePlantSessionPaint()

  const area = data?.area
  const subAreas = React.useMemo(() => data?.subAreas ?? [], [data?.subAreas])
  const equipment = React.useMemo(() => data?.equipment ?? [], [data?.equipment])
  const processes = React.useMemo(() => data?.processes ?? [], [data?.processes])
  const parts = React.useMemo(() => data?.parts ?? [], [data?.parts])
  const faults = React.useMemo(() => data?.faults ?? [], [data?.faults])
  const parents = React.useMemo(() => data?.parents ?? [], [data?.parents])
  const additionalProperties = React.useMemo(
    () =>
      area ? collectAdditionalProperties('Area', area as unknown as Record<string, unknown>) : [],
    [area]
  )

  const areaMark = markFor(decodedAreaId)
  const subAreaGhosts = useVisibleGhosts(decodedAreaId, 'Area')
  const equipmentGhosts = useVisibleGhosts(decodedAreaId, 'Equipment')
  const processGhosts = useVisibleGhosts(decodedAreaId, 'Process')
  const partGhosts = useVisibleGhosts(decodedAreaId, 'Part')

  const subAreaIds = React.useMemo(() => subAreas.map((sub) => sub.id), [subAreas])
  const equipmentIds = React.useMemo(() => equipment.map((item) => item.id), [equipment])
  const processIds = React.useMemo(() => processes.map((proc) => proc.id), [processes])
  const partIds = React.useMemo(() => parts.map((part) => part.id), [parts])

  const Icon = areaKindIcon(area?.area_kind)
  const kindLabel = areaKindLabel(area?.area_kind)
  const tempLabel = temperatureClassLabel(area?.temperature_class)
  const TempIcon = temperatureClassIcon(area?.temperature_class)

  return (
    <div className="flex flex-1 flex-col gap-card">
      <nav className="text-sm text-ink-3" aria-label="Breadcrumb">
        <Link to={plantAreasIndexHref()} className="hover:text-ink hover:underline">
          Areas
        </Link>
        {data?.site && (
          <>
            <span className="mx-1.5 text-ink-4">/</span>
            <PlantHref
              id={data.site.id}
              label="Site"
              node={data.site}
              className="hover:text-ink hover:underline"
            >
              {data.site.name}
            </PlantHref>
          </>
        )}
        {parents.map((parent) => (
          <React.Fragment key={parent.id}>
            <span className="mx-1.5 text-ink-4">/</span>
            <PlantHref
              id={parent.id}
              label="Area"
              node={parent}
              className="hover:text-ink hover:underline"
            >
              {parent.name}
            </PlantHref>
          </React.Fragment>
        ))}
        <span className="mx-1.5 text-ink-4">/</span>
        <span className="text-ink">{area?.name ?? decodedAreaId}</span>
      </nav>

      {isError ? (
        <DetailFallback
          title="Couldn't load this area"
          hint="The area may have been deleted or the proxy is unreachable."
        />
      ) : isLoading || !data ? (
        <DetailFallback title="Loading area…" hint="" muted />
      ) : (
        <>
          <header
            className={sessionDetailHeaderClass(
              'flex items-start gap-4 rounded-xl border bg-card p-5 text-card-foreground shadow-sm',
              areaMark
            )}
          >
            <span className="grid size-11 shrink-0 place-items-center rounded-lg bg-brand-soft text-brand">
              <Icon size={22} />
            </span>
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <h1 className="m-0 text-2xl font-semibold text-ink">
                  {area?.name ?? decodedAreaId}
                </h1>
                <SessionTitleMark mark={areaMark} />
                {kindLabel && <Badge variant="secondary">{kindLabel}</Badge>}
                {tempLabel && (
                  <Badge variant="outline" className="inline-flex items-center gap-1">
                    <TempIcon size={12} aria-hidden />
                    {tempLabel}
                  </Badge>
                )}
                <PlantStrayPill isStray={data.isStray} />
              </div>
              {area?.description && (
                <p className="m-0 mt-2 max-w-3xl text-sm text-ink-3">{area.description}</p>
              )}
            </div>
          </header>

          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <Stat
              label="Sub-areas"
              value={subAreas.length}
              added={active ? countSessionMarks(subAreaIds, markFor, 'added') : 0}
              modified={active ? countSessionMarks(subAreaIds, markFor, 'touched') : 0}
              deleted={subAreaGhosts.filter((ghost) => ghost.view === 'deleted').length}
            />
            <Stat
              label="Equipment"
              value={equipment.length}
              added={active ? countSessionMarks(equipmentIds, markFor, 'added') : 0}
              modified={active ? countSessionMarks(equipmentIds, markFor, 'touched') : 0}
              deleted={equipmentGhosts.filter((ghost) => ghost.view === 'deleted').length}
            />
            <Stat
              label="Processes"
              value={processes.length}
              added={active ? countSessionMarks(processIds, markFor, 'added') : 0}
              modified={active ? countSessionMarks(processIds, markFor, 'touched') : 0}
              deleted={processGhosts.filter((ghost) => ghost.view === 'deleted').length}
            />
            <Stat
              label="Parts"
              value={parts.length}
              added={active ? countSessionMarks(partIds, markFor, 'added') : 0}
              modified={active ? countSessionMarks(partIds, markFor, 'touched') : 0}
              deleted={partGhosts.filter((ghost) => ghost.view === 'deleted').length}
            />
          </div>

          <FaultKnowledgeSection faults={faults} aboutId={decodedAreaId} />

          {(subAreas.length > 0 || subAreaGhosts.length > 0) && (
            <SubAreasSection subAreas={subAreas} ghosts={subAreaGhosts} />
          )}

          <EquipmentSection equipment={equipment} ghosts={equipmentGhosts} />
          <ProcessesSection processes={processes} ghosts={processGhosts} />
          {(parts.length > 0 || partGhosts.length > 0) && (
            <PartsSection parts={parts} ghosts={partGhosts} />
          )}

          <EntityDetailsInspector entity={data.area} additionalProperties={additionalProperties} />
        </>
      )}
    </div>
  )
}

function SubAreasSection({
  subAreas,
  ghosts = []
}: {
  subAreas: PlantAreaSummary[]
  ghosts?: PlantSessionGhostView[]
}): React.ReactElement {
  const total = subAreas.length + ghosts.length

  return (
    <section>
      <SectionHeader label="Sub-areas" subtitle={`${total} ${total === 1 ? 'area' : 'areas'}`} />
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-[repeat(auto-fill,minmax(17.5rem,1fr))]">
        {subAreas.map((sub) => (
          <SubAreaTile key={sub.id} area={sub} />
        ))}
        {ghosts.map((ghost) => (
          <SessionGhostTile
            key={ghost.id}
            name={ghost.name ?? ghost.id}
            view={ghost.view}
            icon={
              <span className="grid size-9 shrink-0 place-items-center rounded-md bg-st-down-bg text-st-down">
                <MapPinAreaIcon size={16} />
              </span>
            }
          />
        ))}
      </div>
    </section>
  )
}

function SubAreaTile({ area }: { area: PlantAreaSummary }): React.ReactElement {
  const Icon = areaKindIcon(area.area_kind)
  const kindLabel = areaKindLabel(area.area_kind)
  const { markFor, rollupByContainerId, touchedRollupByContainerId, deletedRollupByContainerId } =
    usePlantSessionPaint()
  const mark = markFor(area.id)
  const added = rollupByContainerId.get(area.id) ?? 0
  const modified = touchedRollupByContainerId.get(area.id) ?? 0
  const deleted = deletedRollupByContainerId.get(area.id) ?? 0

  return (
    <PlantHref
      id={area.id}
      label="Area"
      node={area}
      className={sessionMarkClass(
        'flex min-w-0 items-center gap-3 rounded-xl border bg-card p-3.5 text-card-foreground no-underline shadow-sm transition-colors hover:bg-accent hover:text-accent-foreground',
        mark
      )}
    >
      <span className="grid size-9 shrink-0 place-items-center rounded-md bg-bg-sunk text-ink-3">
        <Icon size={16} />
      </span>
      <span className="min-w-0 flex-1">
        <span className="flex min-w-0 items-center gap-1.5">
          <span className="block min-w-0 flex-1 truncate text-sm font-semibold text-ink">
            {area.name}
          </span>
          <SessionMarkAffordances mark={mark} />
        </span>
        <span className="mt-1.5 flex flex-wrap items-center gap-1.5">
          {kindLabel && <Badge variant="outline">{kindLabel}</Badge>}
          {area.equipmentCount > 0 && (
            <Badge variant="outline">{area.equipmentCount} equipment</Badge>
          )}
          {area.subAreaCount > 0 && (
            <Badge variant="outline">
              {area.subAreaCount} {area.subAreaCount === 1 ? 'sub-area' : 'sub-areas'}
            </Badge>
          )}
          <SessionRollupPill added={added} modified={modified} deleted={deleted} />
        </span>
      </span>
    </PlantHref>
  )
}

function EquipmentSection({
  equipment,
  ghosts = []
}: {
  equipment: PlantEquipment[]
  ghosts?: PlantSessionGhostView[]
}): React.ReactElement {
  const total = equipment.length + ghosts.length

  return (
    <section>
      <SectionHeader
        label="Equipment located here"
        subtitle={`${total} ${total === 1 ? 'piece' : 'pieces'}`}
      />
      {total === 0 ? (
        <EmptyCard
          title="No equipment in this area"
          hint="Equipment is placed in an area via `LOCATED_IN`."
        />
      ) : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-[repeat(auto-fill,minmax(17.5rem,1fr))]">
          {equipment.map((item) => (
            <EquipmentTile key={item.id} equipment={item} />
          ))}
          {ghosts.map((ghost) => (
            <SessionGhostTile
              key={ghost.id}
              name={ghost.name ?? ghost.id}
              view={ghost.view}
              icon={
                <span className="grid size-9 shrink-0 place-items-center rounded-md bg-st-down-bg text-st-down">
                  <GhostEquipmentIcon size={16} />
                </span>
              }
            />
          ))}
        </div>
      )}
    </section>
  )
}

function EquipmentTile({ equipment }: { equipment: PlantEquipment }): React.ReactElement {
  const Icon = equipmentIconFor(equipment.equipment_type)
  const { markFor } = usePlantSessionPaint()
  const mark = markFor(equipment.id)

  return (
    <PlantHref
      id={equipment.id}
      label="Equipment"
      node={equipment}
      className={sessionMarkClass(
        'flex min-w-0 items-center gap-3 rounded-xl border bg-card p-3.5 text-card-foreground no-underline shadow-sm transition-colors hover:bg-accent hover:text-accent-foreground',
        mark
      )}
    >
      <span className="grid size-9 shrink-0 place-items-center rounded-md bg-bg-sunk text-ink-3">
        <Icon size={16} />
      </span>
      <span className="min-w-0 flex-1">
        <span className="flex min-w-0 items-center gap-1.5">
          <span className="block min-w-0 flex-1 truncate text-sm font-semibold text-ink">
            {equipment.name}
          </span>
          <SessionMarkAffordances mark={mark} />
        </span>
        <span className="mt-1.5 flex flex-wrap items-center gap-1.5">
          {equipment.equipment_type && <Badge variant="outline">{equipment.equipment_type}</Badge>}
          {equipment.vendor && <Badge variant="outline">{equipment.vendor}</Badge>}
        </span>
      </span>
    </PlantHref>
  )
}

function ProcessesSection({
  processes,
  ghosts = []
}: {
  processes: PlantProcess[]
  ghosts?: PlantSessionGhostView[]
}): React.ReactElement {
  const total = processes.length + ghosts.length

  return (
    <section>
      <SectionHeader
        label="Processes located here"
        subtitle={`${total} ${total === 1 ? 'process' : 'processes'}`}
      />
      {total === 0 ? (
        <EmptyCard
          title="No processes in this area"
          hint="Processes are placed in an area via `LOCATED_IN`."
        />
      ) : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-[repeat(auto-fill,minmax(17.5rem,1fr))]">
          {processes.map((proc) => (
            <ProcessTile key={proc.id} process={proc} />
          ))}
          {ghosts.map((ghost) => (
            <SessionGhostTile
              key={ghost.id}
              name={ghost.name ?? ghost.id}
              view={ghost.view}
              icon={
                <span className="grid size-9 shrink-0 place-items-center rounded-md bg-st-down-bg text-st-down">
                  <FlowArrowIcon size={16} />
                </span>
              }
            />
          ))}
        </div>
      )}
    </section>
  )
}

function ProcessTile({ process }: { process: PlantProcess }): React.ReactElement {
  const { markFor } = usePlantSessionPaint()
  const mark = markFor(process.id)

  return (
    <PlantHref
      id={process.id}
      label="Process"
      node={process}
      className={sessionMarkClass(
        'flex min-w-0 items-center gap-3 rounded-xl border bg-card p-3.5 text-card-foreground no-underline shadow-sm transition-colors hover:bg-accent hover:text-accent-foreground',
        mark
      )}
    >
      <span className="grid size-9 shrink-0 place-items-center rounded-md bg-bg-sunk text-ink-3">
        <FlowArrowIcon size={16} />
      </span>
      <span className="min-w-0 flex-1">
        <span className="flex min-w-0 items-center gap-1.5">
          <span className="block min-w-0 flex-1 truncate text-sm font-semibold text-ink">
            {process.name}
          </span>
          <SessionMarkAffordances mark={mark} />
        </span>
        {process.process_type && (
          <span className="mt-1.5 flex flex-wrap items-center gap-1.5">
            <Badge variant="outline">{process.process_type}</Badge>
          </span>
        )}
      </span>
    </PlantHref>
  )
}

function PartsSection({
  parts,
  ghosts = []
}: {
  parts: PlantPartRef[]
  ghosts?: PlantSessionGhostView[]
}): React.ReactElement {
  const total = parts.length + ghosts.length

  return (
    <section>
      <SectionHeader
        label="Parts located here"
        subtitle={`${total} ${total === 1 ? 'part' : 'parts'}`}
      />
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-[repeat(auto-fill,minmax(17.5rem,1fr))]">
        {parts.map((part) => (
          <PartTile key={part.id} part={part} />
        ))}
        {ghosts.map((ghost) => (
          <SessionGhostTile
            key={ghost.id}
            name={ghost.name ?? ghost.id}
            view={ghost.view}
            icon={
              <span className="grid size-9 shrink-0 place-items-center rounded-md bg-st-down-bg text-st-down">
                <PackageIcon size={16} />
              </span>
            }
          />
        ))}
      </div>
    </section>
  )
}

function PartTile({ part }: { part: PlantPartRef }): React.ReactElement {
  const { markFor } = usePlantSessionPaint()
  const mark = markFor(part.id)

  return (
    <PlantHref
      id={part.id}
      label="Part"
      node={part}
      className={sessionMarkClass(
        'flex min-w-0 items-center gap-3 rounded-xl border bg-card p-3.5 text-card-foreground no-underline shadow-sm transition-colors hover:bg-accent hover:text-accent-foreground',
        mark
      )}
    >
      <span className="grid size-9 shrink-0 place-items-center rounded-md bg-bg-sunk text-ink-3">
        <PackageIcon size={16} />
      </span>
      <span className="min-w-0 flex-1">
        <span className="flex min-w-0 items-center gap-1.5">
          <span className="block min-w-0 flex-1 truncate text-sm font-semibold text-ink">
            {part.name}
          </span>
          <SessionMarkAffordances mark={mark} />
        </span>
        <span className="mt-1.5 flex flex-wrap items-center gap-1.5">
          {part.part_type && <Badge variant="outline">{part.part_type}</Badge>}
        </span>
      </span>
    </PlantHref>
  )
}

function Stat({
  label,
  value,
  added = 0,
  modified = 0,
  deleted = 0
}: {
  label: string
  value: number
  added?: number
  modified?: number
  deleted?: number
}): React.ReactElement {
  return (
    <div className="rounded-xl border bg-card p-4 text-card-foreground shadow-sm">
      <div className="flex items-baseline gap-1.5">
        <div className="text-2xl font-semibold tabular-nums text-ink">{value}</div>
        <SessionRollupPill added={added} modified={modified} deleted={deleted} />
      </div>
      <div className="text-11 font-semibold uppercase tracking-[0.04em] text-ink-3">{label}</div>
    </div>
  )
}

function SectionHeader({
  label,
  subtitle
}: {
  label: string
  subtitle?: string
}): React.ReactElement {
  return (
    <div className="mb-2 flex items-baseline gap-3">
      <h3 className="m-0 text-xs font-semibold uppercase tracking-[0.08em] text-ink-3">{label}</h3>
      {subtitle && <span className="text-xs text-ink-3">{subtitle}</span>}
    </div>
  )
}

function EmptyCard({ title, hint }: { title: string; hint: string }): React.ReactElement {
  return (
    <div className="rounded-xl border bg-card p-4 text-sm text-ink-3 shadow-sm">
      <div className="text-ink">{title}</div>
      {hint && <div className="mt-0.5 text-12 text-ink-3">{hint}</div>}
    </div>
  )
}

function DetailFallback({
  title,
  hint,
  muted
}: {
  title: string
  hint: string
  muted?: boolean
}): React.ReactElement {
  return (
    <div className="rounded-xl border bg-card p-6 text-card-foreground shadow-sm">
      {muted ? (
        <div className="text-sm text-ink-3">{title}</div>
      ) : (
        <Empty title={title} hint={hint} icon={WarningCircleIcon} />
      )}
    </div>
  )
}

export default AreaDetailPage
